import parse from 'html-react-parser';
import * as React from 'react';

import { ReadAloudSection } from '../readAloud/readAloudSection';
import { useReadAloud } from '../readAloud/readAloudContext';
import { ScrollToContinueIndicator } from '../shared/scrollToContinueIndicator';

const FILL_IN_BLANK = 'fillInBlank';

const Register = (props: any) => {
  const {
    language,
    languageLocale,
    lesson,
    previewMode,
    resumeActivity,
    session,
    showTranslation,
    startActivity,
    translate,
  } = props;

  const [showIntro, setShowIntro] = React.useState(false);
  const { readAloudEnabled, } = useReadAloud();

  const handleSetShowIntro = React.useCallback(() => {
    setShowIntro(true);
  }, []);

  const landingPageHtmlHasText = React.useCallback(() => {
    return lesson.landingPageHtml.replace(/(<([^>]+)>)/gi, '').length > 0;
  }, [lesson.landingPageHtml]);

  const handleResume = React.useCallback(() => {
    resumeActivity(session);
  }, [resumeActivity, session]);

  const handleStartActivity = React.useCallback(() => {
    if (lesson.landingPageHtml && landingPageHtmlHasText() && !showIntro) {
      setShowIntro(true);
    } else {
      startActivity();
    }
  }, [landingPageHtmlHasText, lesson.landingPageHtml, showIntro, startActivity]);

  React.useEffect(() => {
    if (lesson?.questionType === FILL_IN_BLANK) {
      handleSetShowIntro();
    }
  }, [handleSetShowIntro, lesson?.questionType]);

  const translatedText = React.useMemo((): string => {
    return lesson.translations && lesson.translations[language];
  }, [language, lesson.translations]);

  const renderButton = () => {
    let onClickFn;
    let text;

    if (session) {
      onClickFn = handleResume;
      text = showTranslation ? translate('buttons^resume') : 'Resume';
    } else if (showIntro) {
      onClickFn = handleStartActivity;
      text = showTranslation ? translate('buttons^begin') : 'Begin';
    } else {
      onClickFn = handleStartActivity;
      text = showTranslation ? translate('buttons^start activity') : 'Start activity';
    }

    return (
      <button
        className="quill-button focus-on-light green contained large"
        lang={languageLocale}
        onClick={onClickFn}
        type="button"
      >
        {text}
      </button>
    );
  };

  const renderConnectIntro = () => {
    const listItems = showTranslation
      ? [
        translate('Quill Connect Intro^Combine the sentences together into one sentence.'),
        translate('Quill Connect Intro^You may add or remove words.'),
        translate('Quill Connect Intro^There is often more than one correct answer.'),
        translate('Quill Connect Intro^Remember to use correct spelling, capitalization, and punctuation!'),
      ]
      : [
        'Combine the sentences together into one sentence.',
        'You may add or remove words.',
        'There is often more than one correct answer.',
        'Remember to use correct spelling, capitalization, and punctuation!',
      ];

    const introContent = (
      <React.Fragment>
        <h1>
          {showTranslation
            ? translate('Quill Connect Intro^Welcome to Quill Connect!')
            : 'Welcome to Quill Connect!'}
        </h1>

        <ul className="register-list">
          {listItems.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      </React.Fragment>
    );

    return (
      <div className="landing-page">
        <div className="intro-container" lang={languageLocale}>
          {readAloudEnabled ? <ReadAloudSection content={introContent} key="connect-intro" label="Read this page aloud" /> : introContent}

          {renderButton()}
        </div>
      </div>
    );
  };

  const renderIntro = () => {
    if (!showIntro) {
      return renderConnectIntro();
    }

    return (
      <div className="landing-page">
        <div className="intro-container">
          {readAloudEnabled ? (
            <ReadAloudSection className="landing-page-html" html={lesson.landingPageHtml} key="landing-page" label="Read this page aloud" />
          ) : (
            <div className="landing-page-html">
              {parse(lesson.landingPageHtml)}
            </div>
          )}

          {translatedText && (
            <React.Fragment>
              <hr />
              <div className="landing-page-html" lang={languageLocale}>
                {parse(translatedText)}
              </div>
            </React.Fragment>
          )}

          {renderButton()}
        </div>
      </div>
    );
  };

  return (
    <section className="student">
      {renderIntro()}
      <ScrollToContinueIndicator />
    </section>
  );
};

export { Register };
